import {
  IonButton,
  IonContent,
  IonDatetime,
  IonFooter,
  IonIcon,
  IonItem,
  IonLabel,
  IonModal,
  IonPage,
  IonPopover,
  IonSearchbar,
} from "@ionic/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { BhpThemeHeaderWithBack } from "./BussinessHomePage";
import { useHistory } from "react-router";
import DetailsCard from "../UI/styles/DetailsCard";
import CallWhatsappSelPrompt from "../components/CallWhatsappSelPrompt";
import axios from "axios";
import {
  API_SECURE_KEY,
  getOrderDetailsDB,
  IDIGIZEN_BACKEND_URL,
} from "../types/constants";
import { calendar, filter } from "ionicons/icons";
import { getSessionDetails } from "../common/cookies";
import {
  getOrderLabel,
  getOrderLabelColor,
  OrderStatusType,
} from "../types/data_types";
import { formatDate, searchStringSuccess } from "../common/commonFunctions";
import GlobalPageRefresher from "../UI/GlobalPageRefresher";
import Button from "../components/CustomButton";

const BhpOrderDetails = () => {
  interface BhpOrderDetailsProps {
    orderId: number;
    orderStatus: OrderStatusType;
    orderDate: string;
    customerName: string;
    customerContact: string;
  }

  const history = useHistory();
  const [orderDetails, setOrderDetails] = React.useState<
    BhpOrderDetailsProps[]
  >([]);

  const { token, schoolId } = getSessionDetails();
  const [refresh, setRefresh] = useState({ refresh: false, orderId: 0 });
  const [currentDate, setCurrentDate] = useState<string>(
    new Date().toISOString()
  );

  console.log("Current date: ", currentDate);

  useEffect(() => {
    const dateOnly = new Date(currentDate).toISOString().split("T")[0];
    axios
      .post(
        `${IDIGIZEN_BACKEND_URL}/${getOrderDetailsDB}`,
        {
          id: schoolId,
          date: dateOnly,
        },
        {
          headers: { Authorization: API_SECURE_KEY },
          params: {
            access_token: token,
            token,
          },
        }
      )
      .then((response) => {
        const orderDetailsData: BhpOrderDetailsProps[] = response.data.data.map(
          (order) => ({
            orderId: order.id,
            orderStatus: order.status,
            orderDate: order.date,
            customerName: order.name,
            customerContact: order.phone,
          })
        );
        console.log("Order details response:", orderDetailsData);
        setOrderDetails(orderDetailsData);
      })
      .catch((error) => {
        console.error("Error fetching order details:", error);
      });
    // Cleanup function to reset order details on component unmount
  }, [refresh, currentDate]);

  const [callWhatsappSelPrompt, setCallWhatsappSelPrompt] = useState("");
  const [searchText, setSearchText] = useState("");

  const [showStatuses, setShowStatuses] = useState<OrderStatusType[]>([]);
  const orderDetailsFiltered: BhpOrderDetailsProps[] = useMemo(() => {
    const _orderDetails: BhpOrderDetailsProps[] = orderDetails.filter(
      (order) => {
        return !showStatuses.length || showStatuses.includes(order.orderStatus);
      }
    );
    return _orderDetails.filter((order) => {
      return (
        searchStringSuccess(searchText, order.customerName) ||
        searchStringSuccess(searchText, order.customerContact) ||
        searchStringSuccess(searchText, order.orderId.toString())
      );
    });
  }, [orderDetails, searchText, showStatuses]);

  const [openFilter, setOpenFilter] = useState(false);
  const statusArray = [
    "ALL",
    "CREATED",
    "PARTIALLY_ACCEPTED",
    "ACCEPTED",
    "PAID",
    "COMPLETE",
    "CANCELLED",
    "PROCESSING",
    "READY",
    "PICKED_UP",
    "DECLINED",
  ];

  const statusFilterJsx = (
    <IonPopover
      isOpen={openFilter}
      onDidDismiss={() => {
        setOpenFilter(false);
      }}
    >
      <IonContent>
        {statusArray.map((status, index) => {
          const label =
            status === "ALL" ? "All" : getOrderLabel(status as OrderStatusType);
          const isSelected =
            status === "ALL"
              ? !showStatuses.length
              : showStatuses.includes(status as OrderStatusType);
          return (
            <IonItem
              key={index}
              onClick={() => {
                if (status === "ALL") {
                  setShowStatuses([]);
                  return;
                }

                setShowStatuses((prev) => {
                  if (prev.includes(status as OrderStatusType)) {
                    return prev.filter((s) => s !== status);
                  } else {
                    return [...prev, status as OrderStatusType];
                  }
                });
              }}
            >
              <IonLabel color={isSelected ? "primary" : "medium"}>
                {label}
              </IonLabel>
            </IonItem>
          );
        })}
      </IonContent>
      <IonFooter>
        <IonButton
          color={"danger"}
          expand="block"
          onClick={() => {
            setOpenFilter(false);
          }}
        >
          Close
        </IonButton>
      </IonFooter>
    </IonPopover>
  );

  const dateModalRef = useRef<HTMLIonModalElement>(null);

  const dateTimeModal = (
    <IonModal
      ref={dateModalRef}
      trigger="open-date-modal"
      onDidDismiss={() => {
        dateModalRef.current?.dismiss();
      }}
      breakpoints={[0, 0.25, 0.5, 0.75, 1]}
      initialBreakpoint={0.75}
    >
      <BhpThemeHeaderWithBack
        title="Select Date"
        onBack={() => {
          dateModalRef.current?.dismiss();
        }}
      />
      <div
        style={{
          height: "50%",
          padding: "10px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <IonDatetime
          id="date"
          presentation="date"
          value={currentDate}
          max={new Date().toISOString()}
          onIonChange={(e) => {
            setCurrentDate(e.detail.value as string);
            dateModalRef.current?.dismiss();
          }}
          color="success"
          style={{ color: "var(--ion-color-dark)" }}
        />
      </div>
    </IonModal>
  );

  return (
    <IonPage>
      {statusFilterJsx}
      <CallWhatsappSelPrompt
        onDidDismiss={() => {
          setCallWhatsappSelPrompt("");
        }}
        open={!!callWhatsappSelPrompt}
        phone={callWhatsappSelPrompt}
      />
      {dateTimeModal}
      <BhpThemeHeaderWithBack
        title="Order Details"
        onBack={() => {
          history.goBack();
        }}
        headerTextDisableBottom={false}
      >
        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
          <div style={{ flexGrow: 1 }}>
            <IonSearchbar
              value={searchText}
              onIonChange={(e) => {
                setSearchText(e.detail.value!);
              }}
              placeholder="Search by ID / Name / Phone"
            />
          </div>
          <div style={{ flexShrink: 0 }}>
            <IonButton
              size="small"
              fill="clear"
              color="dark"
              onClick={() => {
                setOpenFilter(true);
              }}
            >
              <IonIcon icon={filter} />
            </IonButton>
          </div>
        </div>
        <div style={{ paddingTop: "10px" }}>
          <Button
            id="open-date-modal"
            style={{
              fontSize: "1rem",
              width: "100%",
              padding: "1.25rem",
              borderRadius: "0px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            My Orders
            <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              {formatDate(currentDate, true)}
              <IonIcon icon={calendar} />
            </span>
          </Button>
        </div>
      </BhpThemeHeaderWithBack>
      <IonContent>
        <GlobalPageRefresher />
        {!orderDetailsFiltered.length && (
          <div>
            <p style={{ textAlign: "center", padding: "10px", color: "gray" }}>
              No Orders Found!
            </p>
          </div>
        )}
        {orderDetailsFiltered.map((order, index) => {
          return (
            <DetailsCard
              key={index}
              muteBg={
                order.orderStatus === "CANCELLED" ||
                order.orderStatus === "COMPLETE" ||
                order.orderStatus === "DECLINED"
              }
              createdOn={order.orderDate}
              id={order.orderId}
              name={{
                label: "Customer Name",
                phone: order.customerContact,
                value: order.customerName,
              }}
              status={getOrderLabel(order.orderStatus)}
              color={getOrderLabelColor(order.orderStatus)}
              callWhatsapp={(phone) => {
                setCallWhatsappSelPrompt(phone);
              }}
              onClick={() => {
                // setExpandOrderId(order.orderId);
                history.push(`/one-order-detail?orderId=${order.orderId}`);
              }}
            >
              {/* <BhpOrderStatusChange
                orderId={order.orderId}
                orderStatus={order.orderStatus as OrderStatusType}
                refreshOrderDetails={() => {
                  setRefresh((prev) => !prev);
                }}
              /> */}
            </DetailsCard>
          );
        })}
      </IonContent>
    </IonPage>
  );
};

export default BhpOrderDetails;
